import {useState} from 'react';

export interface CalculatorParams {
  loanAmount: number;
  interestRate: number;
  termLength: number;
}

export interface Values {
  monthlyPayable?: string;
  monthlyPayableInterest?: string;
  termLength?: string;
  totalPayableInterest?: string;
  totalPayable?: string;
}

interface UseBusinessLoanCalculator {
  submitCalculator: ({interestRate, loanAmount, termLength}: CalculatorParams) => void;
  submitCommercialMortgageCalculator: ({interestRate, loanAmount, termLength}: CalculatorParams) => void;
  clearCalculator: () => void;
  values: Values;
}

const useBusinessLoanCalculator = (): UseBusinessLoanCalculator => {
  const [values, setValues] = useState<Values>({
    monthlyPayable: undefined,
    monthlyPayableInterest: undefined,
    termLength: undefined,
    totalPayableInterest: undefined,
    totalPayable: undefined,
  });

  const submitCalculator = ({interestRate, loanAmount, termLength}: CalculatorParams): void => {
    const interestRatePercentage = interestRate / 100;
    const monthlyInterestRate = interestRatePercentage / 12;
    const monthlyPayable = monthlyInterestRate // it can be 0, in theory
      ? loanAmount * (monthlyInterestRate / (1 - (1 + monthlyInterestRate) ** (-1 * termLength)))
      : loanAmount / termLength;
    const totalPayable = monthlyPayable * termLength;
    const totalPayableInterest = totalPayable - loanAmount;
    const monthlyPayableInterest = totalPayableInterest / termLength;

    setValues({
      monthlyPayable: monthlyPayable.toFixed(2),
      monthlyPayableInterest: monthlyPayableInterest.toFixed(2),
      termLength: termLength.toString(),
      totalPayable: totalPayable.toFixed(2),
      totalPayableInterest: totalPayableInterest.toFixed(2),
    });
  };

  const submitCommercialMortgageCalculator = ({interestRate, loanAmount, termLength}: CalculatorParams): void => {
    const interestRatePercentage = interestRate / 100;
    const monthlyInterestRate = interestRatePercentage / 12;
    const monthlyPayable = monthlyInterestRate // it can be 0, in theory
      ? loanAmount * (monthlyInterestRate / (1 - (1 + monthlyInterestRate) ** (-1 * (termLength * 12))))
      : loanAmount / (termLength * 12);
    const totalPayable = monthlyPayable * (termLength * 12);
    const totalPayableInterest = totalPayable - loanAmount;
    const monthlyPayableInterest = totalPayableInterest / (termLength * 12);

    setValues({
      monthlyPayable: monthlyPayable.toFixed(2),
      monthlyPayableInterest: monthlyPayableInterest.toFixed(2),
      termLength: termLength.toString(),
      totalPayable: totalPayable.toFixed(2),
      totalPayableInterest: totalPayableInterest.toFixed(2),
    });
  };

  const clearCalculator = () => {
    setValues({
      monthlyPayable: undefined,
      monthlyPayableInterest: undefined,
      termLength: undefined,
      totalPayableInterest: undefined,
      totalPayable: undefined,
    });
  };

  return {
    submitCalculator,
    submitCommercialMortgageCalculator,
    values,
    clearCalculator,
  };
};

export default useBusinessLoanCalculator;
